<template>
  <el-dialog
    title="Markdown 預覽與編輯"
    :visible.sync="dialogVisible"
    width="90%"
    top="5vh"
    :modal="true"
    :modal-append-to-body="false"
    :append-to-body="true"
    :close-on-click-modal="false"
    custom-class="markdown-preview-dialog"
    @close="handleClose">
    <div class="preview-container">
      <el-tabs v-model="activeTab" @tab-click="handleTabClick" class="preview-tabs">
        <el-tab-pane label="預覽" name="preview">
          <div class="markdown-preview" v-html="renderedMarkdown"></div>
        </el-tab-pane>
        <el-tab-pane label="編輯" name="edit">
          <el-input
            type="textarea"
            v-model="editableContent"
            :rows="25"
            placeholder="Markdown內容"
            class="markdown-editor">
          </el-input>
        </el-tab-pane>
      </el-tabs>
    </div>
    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" @click="saveChanges">保存修改</el-button>
      <el-button type="success" @click="downloadFile">下載</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { fetchFileContent, downloadFile } from '@/utils/fileUtils'
import { renderMarkdown } from '@/utils/markdownRenderer'
import { Message, Loading } from 'element-ui'

export default {
  name: 'MarkdownPreviewEditor',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    fileUrl: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      dialogVisible: false,
      activeTab: 'preview',
      markdownContent: '',
      editableContent: '',
      originalContent: '',
      renderedMarkdown: ''
    }
  },
  watch: {
    visible: {
      handler(newVal) {
        this.dialogVisible = newVal
        if (newVal && this.fileUrl) {
          this.loadContent()
        }
      },
      immediate: true
    }
  },
  methods: {
    // 加载Markdown内容
    async loadContent() {
      if (!this.fileUrl) {
        Message.warning('Markdown文件不可用')
        return
      }

      try {
        const loading = Loading.service({
          lock: true,
          text: '正在載入Markdown內容...',
          background: 'rgba(255, 255, 255, 0.9)'
        })

        const content = await fetchFileContent(this.fileUrl)
        this.markdownContent = content
        this.editableContent = content
        this.originalContent = content
        
        // 渲染Markdown为HTML
        this.renderedMarkdown = renderMarkdown(content)
        this.activeTab = 'preview'
        
        loading.close()
      } catch (error) {
        console.error('Markdown預覽失敗:', error)
        Message.error('Markdown內容載入失敗，請稍後重試')
      }
    },

    // 处理标签页切换
    handleTabClick(tab) {
      if (tab.name === 'edit') {
        // 切换到编辑模式时，同步当前内容
        this.editableContent = this.markdownContent
      } else if (tab.name === 'preview') {
        // 切换到预览模式时，更新预览内容
        this.markdownContent = this.editableContent
        this.renderedMarkdown = renderMarkdown(this.markdownContent)
      }
    },

    // 保存修改
    saveChanges() {
      this.markdownContent = this.editableContent
      this.renderedMarkdown = renderMarkdown(this.markdownContent)
      this.$emit('content-changed', this.editableContent)
      Message.success('Markdown內容已更新')
    },

    // 下载文件
    downloadFile() {
      try {
        const filename = `edited_markdown_${new Date().getTime()}.md`
        const success = downloadFile(this.editableContent, filename, 'text/markdown')
        if (success) {
          Message.success('Markdown文件下載成功')
        } else {
          Message.error('Markdown文件下載失敗')
        }
      } catch (error) {
        console.error('Markdown下載失敗:', error)
        Message.error('Markdown文件下載失敗')
      }
    },

    // 关闭对话框
    handleClose() {
      this.dialogVisible = false
      this.$emit('update:visible', false)
    }
  }
}
</script>

<style scoped>
/* 预览对话框样式 */
.markdown-preview-dialog {
  border-radius: 8px;
}

.markdown-preview-dialog .el-dialog {
  margin: 5vh auto 0 !important;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.markdown-preview-dialog .el-dialog__body {
  padding: 0 !important;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.preview-container {
  height: 70vh;
  min-height: 500px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.preview-tabs {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.preview-tabs .el-tabs__header {
  margin: 0;
  padding: 0 20px;
  border-bottom: 1px solid #e4e7ed;
  flex-shrink: 0;
}

.preview-tabs .el-tabs__content {
  flex: 1;
  overflow: hidden;
  padding: 0;
}

.preview-tabs .el-tab-pane {
  height: 100%;
  overflow: auto;
  padding: 20px;
}

/* Markdown预览样式 */
.markdown-preview {
  border: 1px solid #e6e6e6;
  border-radius: 4px;
  padding: 20px;
  background: #fff;
  height: calc(100% - 40px);
  overflow-y: auto;
  overflow-x: auto;
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  line-height: 1.6;
  /* 确保滚动条显示 */
  scrollbar-width: thin;
  scrollbar-color: #c0c4cc #f5f7fa;
}

.markdown-preview::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.markdown-preview::-webkit-scrollbar-track {
  background: #f5f7fa;
  border-radius: 4px;
}

.markdown-preview::-webkit-scrollbar-thumb {
  background: #c0c4cc;
  border-radius: 4px;
}

.markdown-preview::-webkit-scrollbar-thumb:hover {
  background: #909399;
}

.markdown-preview h1, .markdown-preview h2, .markdown-preview h3,
.markdown-preview h4, .markdown-preview h5, .markdown-preview h6 {
  margin-top: 24px;
  margin-bottom: 16px;
  font-weight: 600;
  line-height: 1.25;
  color: #24292e;
}

.markdown-preview h1 { 
  font-size: 2em;
  border-bottom: 1px solid #eaecef; 
  padding-bottom: 10px; 
}

.markdown-preview h2 { 
  font-size: 1.5em;
  border-bottom: 1px solid #eaecef; 
  padding-bottom: 8px; 
}

.markdown-preview h3 { font-size: 1.25em; }

.markdown-preview p {
  margin-bottom: 16px;
  color: #24292e;
}

.markdown-preview strong {
  font-weight: 600;
}

.markdown-preview em {
  font-style: italic;
}

.markdown-preview a {
  color: #0366d6;
  text-decoration: none;
}

.markdown-preview a:hover {
  text-decoration: underline;
}

.markdown-preview img {
  max-width: 100%;
  height: auto;
  margin: 10px 0;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.markdown-preview table {
  border-collapse: collapse;
  margin: 20px 0;
  width: 100%;
  border: 1px solid #dfe2e5;
}

.markdown-preview table th,
.markdown-preview table td {
  border: 1px solid #dfe2e5;
  padding: 8px 12px;
  text-align: left;
}

.markdown-preview table th {
  background-color: #f6f8fa;
  font-weight: 600;
}

.markdown-preview table tr:nth-child(even) {
  background-color: #f6f8fa;
}

.markdown-preview pre {
  background-color: #f6f8fa;
  border-radius: 4px;
  padding: 16px;
  overflow-x: auto;
  margin: 16px 0;
}

.markdown-preview code {
  background-color: rgba(175, 184, 193, 0.2);
  padding: 2px 4px;
  border-radius: 3px;
  font-size: 85%;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

.markdown-preview pre code {
  background-color: transparent;
  padding: 0;
}

.markdown-preview ul, .markdown-preview ol {
  margin: 16px 0;
  padding-left: 32px;
}

.markdown-preview li {
  margin: 4px 0;
}

/* 编辑器样式 */
.markdown-editor {
  height: calc(100% - 40px);
  margin: 0;
}

.markdown-editor .el-textarea {
  height: 100%;
}

.markdown-editor .el-textarea__inner {
  height: 100% !important;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
  font-size: 14px !important;
  line-height: 1.5 !important;
  resize: none !important;
  border: 1px solid #e6e6e6;
  border-radius: 4px;
  /* 确保滚动条显示 */
  scrollbar-width: thin;
  scrollbar-color: #c0c4cc #f5f7fa;
}

.markdown-editor .el-textarea__inner::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.markdown-editor .el-textarea__inner::-webkit-scrollbar-track {
  background: #f5f7fa;
  border-radius: 4px;
}

.markdown-editor .el-textarea__inner::-webkit-scrollbar-thumb {
  background: #c0c4cc;
  border-radius: 4px;
}

.markdown-editor .el-textarea__inner::-webkit-scrollbar-thumb:hover {
  background: #909399;
}

.dialog-footer {
  text-align: right;
  padding: 12px 20px;
  border-top: 1px solid #e6e6e6;
}

.dialog-footer .el-button {
  margin-left: 10px;
}
</style>
